import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../contexts/AuthContext';

const PRIORIDADES = [
  { chave: '', label: 'Todas' },
  { chave: 'baixa', label: 'Baixa' },
  { chave: 'media', label: 'Media' },
  { chave: 'alta', label: 'Alta' },
  { chave: 'urgente', label: 'Urgente' },
];

const STATUS_LABEL = {
  aberto: 'Aberto',
  em_atendimento: 'Em atendimento',
  aguardando_usuario: 'Aguardando usuario',
  resolvido: 'Resolvido',
  fechado: 'Fechado',
  cancelado: 'Cancelado',
};

const PRIORIDADE_LABEL = {
  baixa: 'Baixa',
  media: 'Media',
  alta: 'Alta',
  urgente: 'Urgente',
};

const STATUS_COR = {
  aberto: '#fe8d25',
  em_atendimento: '#2A8A9C',
  aguardando_usuario: '#a37bcc',
  resolvido: '#4CB553',
  fechado: '#5E7A82',
  cancelado: '#b43737',
};

function toDateInput(d) {
  const dt = d instanceof Date ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatMin(v) {
  if (v == null || Number.isNaN(v)) return '—';
  if (v < 60) return `${v} min`;
  if (v < 1440) {
    const h = Math.floor(v / 60);
    const m = v % 60;
    return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
  }
  const d = Math.floor(v / 1440);
  const h = Math.floor((v % 1440) / 60);
  return h ? `${d}d ${h}h` : `${d}d`;
}

function pct(v) {
  if (v == null || Number.isNaN(v)) return '—';
  return `${v}%`;
}

function corSla(v) {
  if (v == null) return 'var(--portal-muted)';
  if (v >= 90) return '#4CB553';
  if (v >= 70) return '#fe8d25';
  return '#b43737';
}

// Painel de destaques automaticos: detecta os principais drivers dos KPIs
function Destaques({ data }) {
  const items = [];

  // Urgentes no backlog
  const urg = Number(data.backlog?.urgentes) || 0;
  if (urg > 0) {
    items.push({
      nivel: 'critico',
      icone: 'bi-exclamation-octagon-fill',
      texto: `${urg} chamado${urg > 1 ? 's' : ''} urgente${urg > 1 ? 's' : ''} no backlog atual sem resolucao`,
    });
  }

  // SLA TTFR critico - aponta a prioridade mais problemática
  if (data.sla?.ttfr_perc != null && data.sla.ttfr_perc < 90 && data.sla.ttfr_avaliados >= 2) {
    const pior = (data.sla_por_prioridade || [])
      .filter((p) => p.ttfr_perc != null && p.ttfr_avaliados >= 2)
      .sort((a, b) => a.ttfr_perc - b.ttfr_perc)[0];
    const nivel = data.sla.ttfr_perc < 70 ? 'critico' : 'atencao';
    if (pior) {
      items.push({
        nivel,
        icone: 'bi-clock-history',
        texto: `SLA de 1ª resposta em ${data.sla.ttfr_perc}% — prioridade ${PRIORIDADE_LABEL[pior.prioridade] || pior.prioridade} e a mais critica: ${pior.ttfr_perc}% no SLA (${pior.ttfr_dentro}/${pior.ttfr_avaliados} chamados)`,
      });
    } else {
      items.push({
        nivel,
        icone: 'bi-clock-history',
        texto: `SLA de 1ª resposta em ${data.sla.ttfr_perc}% — abaixo ${data.sla.ttfr_perc < 70 ? 'do limite critico (70%)' : 'da meta (90%)'}`,
      });
    }
  }

  // SLA TTR critico
  if (data.sla?.ttr_perc != null && data.sla.ttr_perc < 90 && data.sla.ttr_avaliados >= 2) {
    const pior = (data.sla_por_prioridade || [])
      .filter((p) => p.ttr_perc != null && p.ttr_avaliados >= 2)
      .sort((a, b) => a.ttr_perc - b.ttr_perc)[0];
    const nivel = data.sla.ttr_perc < 70 ? 'critico' : 'atencao';
    if (pior) {
      items.push({
        nivel,
        icone: 'bi-hourglass-split',
        texto: `SLA de resolucao em ${data.sla.ttr_perc}% — prioridade ${PRIORIDADE_LABEL[pior.prioridade] || pior.prioridade} e a mais critica: ${pior.ttr_perc}% no SLA (${pior.ttr_dentro}/${pior.ttr_avaliados} chamados)`,
      });
    }
  }

  // Categoria dominante (> 35% dos chamados)
  const topCat = data.distribuicoes?.por_categoria?.[0];
  const totalCriados = data.totais?.criados || 0;
  if (topCat && totalCriados >= 5) {
    const percCat = Math.round((topCat.total / totalCriados) * 100);
    if (percCat >= 35) {
      items.push({
        nivel: 'info',
        icone: 'bi-tags',
        texto: `Categoria "${topCat.nome || topCat.categoria}" concentra ${percCat}% dos chamados do periodo (${topCat.total} de ${totalCriados})`,
      });
    }
  }

  // Filial dominante (> 35%)
  const topFilial = data.distribuicoes?.por_filial?.[0];
  if (topFilial && totalCriados >= 5) {
    const percFil = Math.round((topFilial.total / totalCriados) * 100);
    if (percFil >= 35) {
      items.push({
        nivel: 'info',
        icone: 'bi-buildings',
        texto: `Filial "${topFilial.filial}" originou ${percFil}% dos chamados do periodo (${topFilial.total} de ${totalCriados})`,
      });
    }
  }

  // Atendente com pior SLA TTR (min 3 chamados)
  const piorAt = (data.distribuicoes?.por_atendente || [])
    .filter((a) => a.ttr_perc_sla != null && a.total >= 3 && a.ttr_perc_sla < 70)
    .sort((a, b) => a.ttr_perc_sla - b.ttr_perc_sla)[0];
  if (piorAt) {
    items.push({
      nivel: 'atencao',
      icone: 'bi-person-badge',
      texto: `${piorAt.atendente} tem o menor SLA TTR: ${piorAt.ttr_perc_sla}% dentro do limite (${piorAt.total} chamados no periodo)`,
    });
  }

  if (!items.length) return null;

  const COR = { critico: '#b43737', atencao: '#fe8d25', info: '#2A8A9C' };
  const BG  = { critico: 'rgba(180,55,55,0.07)', atencao: 'rgba(254,141,37,0.07)', info: 'rgba(42,138,156,0.07)' };

  return (
    <section className="content-card" style={{ padding: 18, marginTop: 4 }}>
      <h4 style={{ marginBottom: 12, color: 'var(--portal-text)', fontSize: '1rem' }}>
        <i className="bi bi-search me-2" /> Destaques de atencao
      </h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((it, i) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '9px 12px',
            borderRadius: 6,
            borderLeft: `3px solid ${COR[it.nivel]}`,
            background: BG[it.nivel],
          }}>
            <i className={`bi ${it.icone}`} style={{ color: COR[it.nivel], marginTop: 1, flexShrink: 0 }} />
            <span style={{ fontSize: '0.84rem', color: 'var(--portal-text)', lineHeight: 1.4 }}>
              {it.texto}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

// Card de KPI generico
function KpiCard({ label, valor, tom, hint, ariaLabel }) {
  const cor = tom || '#2A8A9C';
  return (
    <article
      className="content-card"
      style={{ padding: 18, borderLeft: `4px solid ${cor}` }}
      aria-label={ariaLabel || label}
    >
      <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', marginBottom: 6 }}>
        {label}
      </div>
      <strong style={{ fontSize: '1.7rem', display: 'block', color: 'var(--portal-text)' }}>
        {valor}
      </strong>
      {hint ? (
        <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)', marginTop: 4 }}>
          {hint}
        </div>
      ) : null}
    </article>
  );
}

// Barra horizontal para distribuicoes
function BarRow({ label, valor, total, cor, sub }) {
  const perc = total > 0 ? Math.round((valor / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 3 }}>
        <span style={{ color: 'var(--portal-text)' }}>{label}</span>
        <span style={{ color: 'var(--portal-muted)' }}>
          {valor}
          {sub ? <span style={{ marginLeft: 6, fontSize: '0.72rem' }}>{sub}</span> : null}
        </span>
      </div>
      <div style={{
        height: 8,
        background: 'var(--portal-surface-soft)',
        borderRadius: 4,
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${perc}%`,
          height: '100%',
          background: cor || '#2A8A9C',
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
}

// Linha temporal SVG (criados vs resolvidos)
function TendenciaSVG({ dados }) {
  if (!dados.length) {
    return (
      <div style={{ padding: 20, color: 'var(--portal-muted)', fontSize: '0.82rem', textAlign: 'center' }}>
        Sem dados no periodo.
      </div>
    );
  }
  const w = 720;
  const h = 200;
  const padL = 36;
  const padR = 12;
  const padT = 16;
  const padB = 28;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;

  const max = Math.max(1, ...dados.map((d) => Math.max(d.criados, d.resolvidos)));
  const stepX = dados.length > 1 ? innerW / (dados.length - 1) : 0;

  const px = (i) => padL + i * stepX;
  const py = (v) => padT + innerH - (v / max) * innerH;

  const linha = (key) => dados
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${px(i).toFixed(1)} ${py(d[key]).toFixed(1)}`)
    .join(' ');

  // 4 grid lines (incluindo base)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((p) => Math.round(max * p));

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="auto">
      {/* Grid */}
      {ticks.map((t, i) => {
        const y = padT + innerH - (t / max) * innerH;
        return (
          <g key={i}>
            <line x1={padL} x2={w - padR} y1={y} y2={y}
              stroke="rgba(143,165,172,0.15)" strokeDasharray="2 4" />
            <text x={padL - 4} y={y + 4} textAnchor="end"
              fontSize="10" fill="var(--portal-muted)">{t}</text>
          </g>
        );
      })}

      {/* Linha criados */}
      <path d={linha('criados')} fill="none" stroke="#2A8A9C" strokeWidth="2" />
      {/* Linha resolvidos */}
      <path d={linha('resolvidos')} fill="none" stroke="#4CB553" strokeWidth="2" />

      {/* Pontos + tooltip */}
      {dados.map((d, i) => (
        <g key={i}>
          <circle cx={px(i)} cy={py(d.criados)} r="3" fill="#2A8A9C">
            <title>{`${d.dia} — ${d.criados} criados`}</title>
          </circle>
          <circle cx={px(i)} cy={py(d.resolvidos)} r="3" fill="#4CB553">
            <title>{`${d.dia} — ${d.resolvidos} resolvidos`}</title>
          </circle>
        </g>
      ))}

      {/* Eixo X (datas) — mostra primeira, ultima e o meio */}
      {[0, Math.floor(dados.length / 2), dados.length - 1]
        .filter((i, idx, arr) => arr.indexOf(i) === idx && i >= 0 && i < dados.length)
        .map((i) => (
          <text key={i} x={px(i)} y={h - 8} textAnchor="middle"
            fontSize="10" fill="var(--portal-muted)">
            {dados[i].dia.slice(5)}
          </text>
        ))}

      {/* Legenda */}
      <g transform={`translate(${padL}, ${padT - 4})`}>
        <circle cx="4" cy="-4" r="3" fill="#2A8A9C" />
        <text x="12" y="-1" fontSize="10" fill="var(--portal-text)">Criados</text>
        <circle cx="74" cy="-4" r="3" fill="#4CB553" />
        <text x="82" y="-1" fontSize="10" fill="var(--portal-text)">Resolvidos</text>
      </g>
    </svg>
  );
}

// Cabecalho de coluna clicavel para ordenacao
function Th({ col, label, sortCol, sortDir, onSort, align }) {
  const ativo = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: align || 'right',
        whiteSpace: 'nowrap',
        color: ativo ? 'var(--portal-text)' : undefined,
      }}
    >
      {label}
      {ativo
        ? <span style={{ marginLeft: 4, opacity: 0.7 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>
        : <span style={{ marginLeft: 4, opacity: 0.25 }}>↕</span>}
    </th>
  );
}

// Tabela analitica reutilizavel: volume + TTFR/TTR/SLA por dimensao
// (categoria ou filial), com ordenacao por qualquer coluna.
function TabelaAnalitica({ titulo, icone, dados, chave, labelChave }) {
  const [sortCol, setSortCol] = useState('total');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    return [...(dados || [])].sort((a, b) => {
      const va = a[sortCol] ?? (sortDir === 'desc' ? -Infinity : Infinity);
      const vb = b[sortCol] ?? (sortDir === 'desc' ? -Infinity : Infinity);
      return sortDir === 'desc' ? vb - va : va - vb;
    });
  }, [dados, sortCol, sortDir]);

  const onSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortCol(col); setSortDir('desc'); }
  };

  const thProps = { sortCol, sortDir, onSort };

  if (!dados || dados.length === 0) {
    return (
      <section className="content-card" style={{ padding: 18 }}>
        <h5 style={{ color: 'var(--portal-text)' }}>
          <i className={`bi ${icone} me-2`} /> {titulo}
        </h5>
        <div className="cell-muted" style={{ fontSize: '0.85rem' }}>Sem dados no periodo.</div>
      </section>
    );
  }

  return (
    <section className="content-card" style={{ padding: 18 }}>
      <h5 style={{ color: 'var(--portal-text)', marginBottom: 12 }}>
        <i className={`bi ${icone} me-2`} /> {titulo}
      </h5>
      <div className="table-shell">
        <table className="portal-table">
          <thead>
            <tr>
              <Th col={chave} label={labelChave} align="left" {...thProps} />
              <Th col="total" label="Chamados" {...thProps} />
              <Th col="ttfr_min_medio" label="TTFR Medio" {...thProps} />
              <Th col="ttfr_min_mediana" label="Mediana" {...thProps} />
              <Th col="ttfr_perc" label="% SLA TTFR" {...thProps} />
              <Th col="ttr_min_medio" label="TTR Medio" {...thProps} />
              <Th col="ttr_min_mediana" label="Mediana" {...thProps} />
              <Th col="ttr_perc" label="% SLA TTR" {...thProps} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr key={row[chave] || row.nome}>
                <td style={{ fontWeight: 500 }}>{row.nome || row[chave]}</td>
                <td style={{ textAlign: 'right' }}>{row.total}</td>
                <td style={{ textAlign: 'right' }}>{formatMin(row.ttfr_min_medio)}</td>
                <td style={{ textAlign: 'right', color: 'var(--portal-muted)', fontSize: '0.8rem' }}>
                  {formatMin(row.ttfr_min_mediana)}
                </td>
                <td style={{ textAlign: 'right', fontWeight: 600, color: corSla(row.ttfr_perc) }}>
                  {row.ttfr_avaliados > 0
                    ? <span title={`${row.ttfr_dentro}/${row.ttfr_avaliados} chamados`}>{pct(row.ttfr_perc)}</span>
                    : <span style={{ color: 'var(--portal-muted)', fontWeight: 400 }}>—</span>}
                </td>
                <td style={{ textAlign: 'right' }}>{formatMin(row.ttr_min_medio)}</td>
                <td style={{ textAlign: 'right', color: 'var(--portal-muted)', fontSize: '0.8rem' }}>
                  {formatMin(row.ttr_min_mediana)}
                </td>
                <td style={{ textAlign: 'right', fontWeight: 600, color: corSla(row.ttr_perc) }}>
                  {row.ttr_avaliados > 0
                    ? <span title={`${row.ttr_dentro}/${row.ttr_avaliados} chamados`}>{pct(row.ttr_perc)}</span>
                    : <span style={{ color: 'var(--portal-muted)', fontWeight: 400 }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 6, fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
        Clique em qualquer cabecalho para ordenar. % SLA calculado apenas para chamados com SLA configurado para a prioridade.
      </div>
    </section>
  );
}

const ABAS_ANALITICA = [
  { chave: 'por_categoria', label: 'Categoria', icone: 'bi-tags',        labelChave: 'Categoria' },
  { chave: 'por_filial',    label: 'Filial',    icone: 'bi-buildings',   labelChave: 'Filial'    },
  { chave: 'por_setor',     label: 'Setor',     icone: 'bi-diagram-3',   labelChave: 'Setor'     },
  { chave: 'por_atendente', label: 'Atendente', icone: 'bi-person-badge',labelChave: 'Atendente' },
];

function VisaoAnalitica({ analise }) {
  const [abaAtiva, setAbaAtiva] = useState('por_categoria');
  const aba = ABAS_ANALITICA.find((a) => a.chave === abaAtiva) || ABAS_ANALITICA[0];

  return (
    <div
      style={{
        marginTop: 8,
        border: '1px solid var(--portal-border)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* Cabecalho */}
      <div style={{
        padding: '12px 18px 0',
        background: 'var(--portal-surface)',
        borderBottom: '1px solid var(--portal-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <i className="bi bi-bar-chart-steps" style={{ color: '#2A8A9C' }} />
          <span style={{ fontWeight: 600, color: 'var(--portal-text)' }}>Visao Analitica</span>
          <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
            TTFR · TTR · % SLA
          </span>
        </div>
        {/* Abas */}
        <div style={{ display: 'flex', gap: 2 }}>
          {ABAS_ANALITICA.map((a) => {
            const ativa = a.chave === abaAtiva;
            return (
              <button
                key={a.chave}
                onClick={() => setAbaAtiva(a.chave)}
                style={{
                  padding: '6px 16px',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  fontWeight: ativa ? 600 : 400,
                  fontSize: '0.85rem',
                  background: ativa ? 'var(--portal-bg)' : 'transparent',
                  color: ativa ? '#2A8A9C' : 'var(--portal-muted)',
                  borderBottom: ativa ? '2px solid #2A8A9C' : '2px solid transparent',
                  transition: 'color 0.15s, border-color 0.15s',
                }}
              >
                <i className={`bi ${a.icone} me-1`} />
                {a.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Conteudo da aba ativa */}
      <div style={{ padding: '12px 16px 16px', background: 'var(--portal-bg)' }}>
        <TabelaAnalitica
          titulo={`Por ${aba.label.toLowerCase()}`}
          icone={aba.icone}
          dados={analise?.[abaAtiva] || []}
          chave={abaAtiva.replace('por_', '')}
          labelChave={aba.labelChave}
        />
      </div>
    </div>
  );
}

function MultiSelect({ placeholder, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const label = value.length ? `${value.length} selecionado(s)` : placeholder;
  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', padding: 0,
          background: 'transparent', border: 'none',
          cursor: 'pointer', textAlign: 'left', fontSize: '0.9rem',
          color: value.length ? 'var(--portal-text)' : 'var(--portal-muted)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          outline: 'none',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ flexShrink: 0, fontSize: '0.7rem' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 200, minWidth: '100%',
          maxHeight: 200, overflowY: 'auto', background: 'var(--portal-surface)',
          border: '1px solid var(--portal-border)', borderRadius: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', marginTop: 2,
        }}>
          {options.length === 0
            ? <div style={{ padding: '6px 10px', fontSize: '0.8rem', color: 'var(--portal-muted)' }}>Sem opcoes</div>
            : options.map((o) => (
              <label key={o.value} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px',
                cursor: 'pointer', fontSize: '0.84rem', color: 'var(--portal-text)',
                background: value.includes(o.value) ? 'rgba(42,138,156,0.08)' : 'transparent',
                userSelect: 'none',
              }}>
                <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} style={{ flexShrink: 0, margin: 0 }} />
                <span>{o.label}</span>
              </label>
            ))}
        </div>
      )}
    </div>
  );
}

export default function Indicadores() {
  const { temPermissao } = useAuth();
  const podeVer = temPermissao('ti:atendimentos', 'admin:atendimento', 'ti:dashboard');

  const hoje = new Date();
  const inicioPadrao = new Date();
  inicioPadrao.setDate(hoje.getDate() - 30);

  const [inicio, setInicio] = useState(toDateInput(inicioPadrao));
  const [fim, setFim] = useState(toDateInput(hoje));
  const [prioridade, setPrioridade] = useState('');
  const [categoria, setCategoria] = useState('');
  const [filial, setFilial] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const [categoriasDisp, setCategoriasDisp] = useState([]);
  const [filiaisDisp, setFiliaisDisp] = useState([]);
  const [canalId, setCanalId] = useState('');
  const [atendentes, setAtendentes] = useState([]);
  const [canaisDisp, setCanaisDisp] = useState([]);
  const [atendentesDisp, setAtendentesDisp] = useState([]);

  // Carrega catalogos para os filtros (categorias, filiais, canais e atendentes)
  useEffect(() => {
    if (!podeVer) return;
    Promise.allSettled([
      axios.get(`${API}/atendimento/categorias?ativos=true`),
      axios.get(`${API}/atendimento/filiais?ativos=true`),
      axios.get(`${API}/atendimento/canais`),
      axios.get(`${API}/metricas/atendentes`),
    ]).then(([rc, rf, rcan, rat]) => {
      if (rc.status === 'fulfilled') setCategoriasDisp(rc.value.data?.data || []);
      if (rf.status === 'fulfilled') setFiliaisDisp(rf.value.data?.data || []);
      if (rcan.status === 'fulfilled') setCanaisDisp(rcan.value.data?.data || []);
      if (rat.status === 'fulfilled') setAtendentesDisp((rat.value.data?.data || []).map((a) => ({ value: a, label: a })));
    });
  }, [podeVer]);

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const params = { inicio, fim };
      if (prioridade) params.prioridade = prioridade;
      if (categoria) params.categoria = categoria;
      if (filial) params.filial = filial;
      if (canalId) params.canal_id = canalId;
      if (atendentes.length) params.atendentes = atendentes.join(',');
      const res = await axios.get(`${API}/metricas/atendimento`, { params });
      setData(res.data?.data || null);
    } catch (err) {
      setErro(err.response?.data?.message || 'Nao foi possivel carregar os indicadores.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!podeVer) return;
    carregar();
  }, []); // carga inicial - filtros sao aplicados pelo botao Aplicar

  const totalParaPercDist = useMemo(() => {
    if (!data) return 0;
    return data.totais?.criados || 0;
  }, [data]);

  if (!podeVer) {
    return (
      <div className="content-shell">
        <div className="feedback-banner feedback-banner--error">
          Voce nao tem permissao para ver os indicadores.
        </div>
      </div>
    );
  }

  const aplicarRapido = (dias) => {
    const ini = new Date();
    ini.setDate(ini.getDate() - dias);
    setInicio(toDateInput(ini));
    setFim(toDateInput(new Date()));
  };

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">Atendimento</span>
          <h1>Indicadores</h1>
          <p>KPIs do atendimento por periodo, com TTFR/TTR e cumprimento de SLA por prioridade.</p>
        </div>
      </section>

      <div className="filter-card">
        <div className="filter-card__field">
          <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>De</label>
          <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
        </div>
        <div className="filter-card__field">
          <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Ate</label>
          <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} />
        </div>
        <div className="filter-card__field">
          <select value={prioridade} onChange={(e) => setPrioridade(e.target.value)}>
            {PRIORIDADES.map((p) => <option key={p.chave} value={p.chave}>{p.label}</option>)}
          </select>
        </div>
        <div className="filter-card__field">
          <select value={categoria} onChange={(e) => setCategoria(e.target.value)}>
            <option value="">Todas categorias</option>
            {categoriasDisp.map((c) => <option key={c.chave} value={c.chave}>{c.nome}</option>)}
          </select>
        </div>
        <div className="filter-card__field">
          <select value={filial} onChange={(e) => setFilial(e.target.value)}>
            <option value="">Todas filiais</option>
            {filiaisDisp.map((f) => (
              <option key={f.id} value={f.nome}>
                {f.codigo ? `${f.codigo} - ` : ''}{f.nome}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-card__field">
          <select value={canalId} onChange={(e) => setCanalId(e.target.value)}>
            <option value="">Todos os canais</option>
            {canaisDisp.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
        <div className="filter-card__field">
          <MultiSelect
            placeholder="Todos atendentes"
            options={atendentesDisp}
            value={atendentes}
            onChange={setAtendentes}
          />
        </div>
        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={carregar}
            style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
            disabled={loading}
          >
            <i className="bi bi-funnel me-1" />
            {loading ? 'Carregando...' : 'Aplicar'}
          </button>
          <button type="button" className="btn btn-sm btn-outline-info" onClick={() => aplicarRapido(7)}>7d</button>
          <button type="button" className="btn btn-sm btn-outline-info" onClick={() => aplicarRapido(30)}>30d</button>
          <button type="button" className="btn btn-sm btn-outline-info" onClick={() => aplicarRapido(90)}>90d</button>
        </div>
      </div>

      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      {loading ? (
        <div className="content-card content-card--centered">
          <div className="screen-loader__spinner" />
          <p>Carregando indicadores...</p>
        </div>
      ) : !data ? (
        <div className="empty-state">
          <strong>Sem dados</strong>
          <span>Aplique os filtros para ver os indicadores.</span>
        </div>
      ) : (
        <>
          {/* KPIs principais */}
          <section className="row g-3" style={{ margin: 0 }}>
            <div className="col-12 col-md-6 col-lg-3">
              <KpiCard label="Chamados criados" valor={data.totais.criados} tom="#2A8A9C"
                hint="No periodo selecionado" />
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <KpiCard label="Resolvidos" valor={data.totais.resolvidos} tom="#4CB553"
                hint={data.totais.criados ? `${Math.round((data.totais.resolvidos / data.totais.criados) * 100)}% dos criados` : '—'} />
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <KpiCard label="Cancelados" valor={data.totais.cancelados} tom="#b43737"
                hint={data.totais.criados ? `${Math.round((data.totais.cancelados / data.totais.criados) * 100)}% dos criados` : '—'} />
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <KpiCard
                label="Backlog (agora)"
                valor={data.backlog.total || 0}
                tom="#fe8d25"
                hint={`${data.backlog.urgentes || 0} urgente(s) - aberto/em atendimento/aguardando`}
              />
            </div>
          </section>

          {/* Tempos + SLA */}
          <section className="row g-3" style={{ margin: '4px 0 0' }}>
            <div className="col-12 col-md-6 col-lg-3">
              <KpiCard label="TTFR medio"
                valor={formatMin(data.tempos.ttfr_min_medio)}
                tom="#2A8A9C"
                hint={`mediana: ${formatMin(data.tempos.ttfr_min_mediana)} - ${data.tempos.com_primeira_resposta} chamado(s) avaliado(s)`} />
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <KpiCard label="TTR medio"
                valor={formatMin(data.tempos.ttr_min_medio)}
                tom="#4CB553"
                hint={`mediana: ${formatMin(data.tempos.ttr_min_mediana)} - ${data.tempos.com_resolucao} resolvido(s)`} />
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <KpiCard label="% no SLA TTFR"
                valor={pct(data.sla.ttfr_perc)}
                tom={data.sla.ttfr_perc != null && data.sla.ttfr_perc >= 90 ? '#4CB553' : data.sla.ttfr_perc != null && data.sla.ttfr_perc >= 70 ? '#fe8d25' : '#b43737'}
                hint={`${data.sla.ttfr_dentro}/${data.sla.ttfr_avaliados} dentro do limite por prioridade`} />
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <KpiCard label="% no SLA TTR"
                valor={pct(data.sla.ttr_perc)}
                tom={data.sla.ttr_perc != null && data.sla.ttr_perc >= 90 ? '#4CB553' : data.sla.ttr_perc != null && data.sla.ttr_perc >= 70 ? '#fe8d25' : '#b43737'}
                hint={`${data.sla.ttr_dentro}/${data.sla.ttr_avaliados} dentro do limite por prioridade`} />
            </div>
          </section>

          {/* Destaques automaticos */}
          <Destaques data={data} />

          {/* Tendencia diaria */}
          <section className="content-card" style={{ padding: 18, marginTop: 4 }}>
            <h4 style={{ marginBottom: 8, color: 'var(--portal-text)' }}>
              <i className="bi bi-graph-up me-2" /> Tendencia diaria
            </h4>
            <TendenciaSVG dados={data.tendencia} />
          </section>

          {/* Visao Analitica: TTFR/TTR/SLA com menu de abas */}
          <VisaoAnalitica analise={data.analise} />

          {/* Distribuicoes em duas colunas */}
          <div className="row g-3" style={{ margin: 0 }}>
            <div className="col-12 col-lg-6">
              <section className="content-card" style={{ padding: 18 }}>
                <h5 style={{ color: 'var(--portal-text)' }}>
                  <i className="bi bi-pie-chart me-2" /> Por status
                </h5>
                {data.distribuicoes.por_status.length === 0 ? (
                  <div className="cell-muted" style={{ fontSize: '0.85rem' }}>Sem dados.</div>
                ) : data.distribuicoes.por_status.map((s) => (
                  <BarRow key={s.status} label={STATUS_LABEL[s.status] || s.status}
                    valor={s.total} total={totalParaPercDist}
                    cor={STATUS_COR[s.status] || '#2A8A9C'} />
                ))}
              </section>
            </div>

            <div className="col-12 col-lg-6">
              <section className="content-card" style={{ padding: 18 }}>
                <h5 style={{ color: 'var(--portal-text)' }}>
                  <i className="bi bi-flag me-2" /> Por prioridade
                </h5>
                {data.distribuicoes.por_prioridade.length === 0 ? (
                  <div className="cell-muted" style={{ fontSize: '0.85rem' }}>Sem dados.</div>
                ) : data.distribuicoes.por_prioridade.map((p) => (
                  <BarRow key={p.prioridade}
                    label={PRIORIDADE_LABEL[p.prioridade] || p.prioridade}
                    valor={p.total}
                    total={totalParaPercDist}
                    cor={p.prioridade === 'urgente' ? '#b43737' : p.prioridade === 'alta' ? '#fe8d25' : '#2A8A9C'} />
                ))}
              </section>
            </div>

            <div className="col-12 col-lg-6">
              <section className="content-card" style={{ padding: 18 }}>
                <h5 style={{ color: 'var(--portal-text)' }}>
                  <i className="bi bi-tags me-2" /> Top categorias
                </h5>
                {data.distribuicoes.por_categoria.length === 0 ? (
                  <div className="cell-muted" style={{ fontSize: '0.85rem' }}>Sem dados.</div>
                ) : data.distribuicoes.por_categoria.slice(0, 8).map((c) => (
                  <BarRow key={c.categoria} label={c.nome || c.categoria}
                    valor={c.total} total={totalParaPercDist} cor="#2A8A9C" />
                ))}
              </section>
            </div>

            <div className="col-12 col-lg-6">
              <section className="content-card" style={{ padding: 18 }}>
                <h5 style={{ color: 'var(--portal-text)' }}>
                  <i className="bi bi-buildings me-2" /> Top filiais
                </h5>
                {data.distribuicoes.por_filial.length === 0 ? (
                  <div className="cell-muted" style={{ fontSize: '0.85rem' }}>Sem dados.</div>
                ) : data.distribuicoes.por_filial.slice(0, 8).map((f) => (
                  <BarRow key={f.filial} label={f.filial}
                    valor={f.total} total={totalParaPercDist} cor="#a37bcc" />
                ))}
              </section>
            </div>

            <div className="col-12 col-lg-6">
              <section className="content-card" style={{ padding: 18 }}>
                <h5 style={{ color: 'var(--portal-text)' }}>
                  <i className="bi bi-diagram-3 me-2" /> Top setores
                </h5>
                {data.distribuicoes.por_setor.length === 0 ? (
                  <div className="cell-muted" style={{ fontSize: '0.85rem' }}>Sem dados.</div>
                ) : data.distribuicoes.por_setor.slice(0, 8).map((s) => (
                  <BarRow key={s.setor} label={s.setor}
                    valor={s.total} total={totalParaPercDist} cor="#fe8d25" />
                ))}
              </section>
            </div>

            <div className="col-12 col-lg-6">
              <section className="content-card" style={{ padding: 18 }}>
                <h5 style={{ color: 'var(--portal-text)' }}>
                  <i className="bi bi-flag-fill me-2" /> Motivos de fechamento
                </h5>
                {data.distribuicoes.motivos_fechamento.length === 0 ? (
                  <div className="cell-muted" style={{ fontSize: '0.85rem' }}>Sem dados.</div>
                ) : data.distribuicoes.motivos_fechamento.slice(0, 8).map((m, idx) => (
                  <BarRow key={idx} label={m.motivo}
                    valor={m.total} total={data.totais.resolvidos + data.totais.fechados} cor="#4CB553" />
                ))}
              </section>
            </div>

            <div className="col-12">
              <section className="content-card" style={{ padding: 18 }}>
                <h5 style={{ color: 'var(--portal-text)' }}>
                  <i className="bi bi-person-badge me-2" /> Performance dos atendentes
                </h5>
                {data.distribuicoes.por_atendente.length === 0 ? (
                  <div className="cell-muted" style={{ fontSize: '0.85rem' }}>Nenhum chamado com atendente atribuido no periodo.</div>
                ) : (
                  <div className="table-shell">
                    <table className="portal-table">
                      <thead>
                        <tr>
                          <th>Atendente</th>
                          <th style={{ textAlign: 'right' }}>Total</th>
                          <th style={{ textAlign: 'right' }}>Resolvidos</th>
                          <th style={{ textAlign: 'right' }}>TTFR medio</th>
                          <th style={{ textAlign: 'right' }}>TTR medio</th>
                          <th style={{ textAlign: 'right' }}>% SLA TTFR</th>
                          <th style={{ textAlign: 'right' }}>% SLA TTR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.distribuicoes.por_atendente.map((a) => (
                          <tr key={a.atendente}>
                            <td>{a.atendente}</td>
                            <td style={{ textAlign: 'right' }}>{a.total}</td>
                            <td style={{ textAlign: 'right' }}>{a.resolvidos}</td>
                            <td style={{ textAlign: 'right' }}>{formatMin(a.ttfr_min_medio)}</td>
                            <td style={{ textAlign: 'right' }}>{formatMin(a.ttr_min_medio)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: corSla(a.ttfr_perc_sla) }}>{pct(a.ttfr_perc_sla)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: corSla(a.ttr_perc_sla) }}>{pct(a.ttr_perc_sla)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>

            <div className="col-12 col-lg-6">
              <section className="content-card" style={{ padding: 18 }}>
                <h5 style={{ color: 'var(--portal-text)' }}>
                  <i className="bi bi-broadcast me-2" /> Origem
                </h5>
                <BarRow label="Via WhatsApp"
                  valor={data.distribuicoes.origem.whatsapp}
                  total={data.distribuicoes.origem.whatsapp + data.distribuicoes.origem.manual}
                  cor="#25D366" />
                <BarRow label="Manual (portal)"
                  valor={data.distribuicoes.origem.manual}
                  total={data.distribuicoes.origem.whatsapp + data.distribuicoes.origem.manual}
                  cor="#5E7A82" />
              </section>
            </div>

            <div className="col-12">
              <section className="content-card" style={{ padding: 18 }}>
                <h5 style={{ color: 'var(--portal-text)' }}>
                  <i className="bi bi-stopwatch me-2" /> SLA por prioridade — meta vs. cumprimento
                </h5>
                <div className="table-shell">
                  <table className="portal-table" style={{ minWidth: 0 }}>
                    <thead>
                      <tr>
                        <th>Prioridade</th>
                        <th style={{ textAlign: 'right' }}>Limite TTFR</th>
                        <th style={{ textAlign: 'right' }}>% TTFR no SLA</th>
                        <th style={{ textAlign: 'right' }}>Avaliados</th>
                        <th style={{ textAlign: 'right' }}>Limite TTR</th>
                        <th style={{ textAlign: 'right' }}>% TTR no SLA</th>
                        <th style={{ textAlign: 'right' }}>Avaliados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.sla_por_prioridade || []).map((p) => (
                        <tr key={p.prioridade}>
                          <td style={{ fontWeight: 600 }}>{PRIORIDADE_LABEL[p.prioridade] || p.prioridade}</td>
                          <td style={{ textAlign: 'right', color: 'var(--portal-muted)' }}>{formatMin(p.ttfr_limite_min)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: corSla(p.ttfr_perc) }}>
                            {pct(p.ttfr_perc)}
                          </td>
                          <td style={{ textAlign: 'right', color: 'var(--portal-muted)', fontSize: '0.8rem' }}>
                            {p.ttfr_avaliados > 0 ? `${p.ttfr_dentro}/${p.ttfr_avaliados}` : '—'}
                          </td>
                          <td style={{ textAlign: 'right', color: 'var(--portal-muted)' }}>{formatMin(p.ttr_limite_min)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: corSla(p.ttr_perc) }}>
                            {pct(p.ttr_perc)}
                          </td>
                          <td style={{ textAlign: 'right', color: 'var(--portal-muted)', fontSize: '0.8rem' }}>
                            {p.ttr_avaliados > 0 ? `${p.ttr_dentro}/${p.ttr_avaliados}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          </div>

          {/* Legenda - explica cada termo. Fica recolhida por padrao para nao
              poluir, mas sempre acessivel para quem nao conhece os indicadores. */}
          <details
            style={{
              marginTop: 8,
              padding: '10px 14px',
              border: '1px dashed var(--portal-border)',
              borderRadius: 8,
              background: 'transparent',
              fontSize: '0.82rem',
              color: 'var(--portal-muted)',
            }}
          >
            <summary style={{ cursor: 'pointer', userSelect: 'none', color: 'var(--portal-text)' }}>
              <i className="bi bi-info-circle me-2" />
              O que cada indicador significa?
            </summary>
            <dl style={{
              marginTop: 12,
              marginBottom: 0,
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              gap: '6px 14px',
              lineHeight: 1.5,
            }}>
              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>Criados</dt>
              <dd style={{ margin: 0 }}>Chamados abertos no periodo selecionado.</dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>Resolvidos</dt>
              <dd style={{ margin: 0 }}>Chamados criados no periodo cujo status atual e <em>resolvido</em>.</dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>Cancelados</dt>
              <dd style={{ margin: 0 }}>Chamados criados no periodo cujo status atual e <em>cancelado</em>.</dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>Backlog</dt>
              <dd style={{ margin: 0 }}>
                Foto do <em>agora</em>: chamados nos status <em>aberto</em>, <em>em atendimento</em> ou
                <em> aguardando usuario</em>. Nao depende do filtro de data — so dos demais filtros.
              </dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>TTFR</dt>
              <dd style={{ margin: 0 }}>
                <strong>Time to First Response</strong> — tempo <em>util</em> entre a criacao do
                chamado e a primeira mensagem do atendente vinculada ao chamado (mensagens do bot
                nao contam). Conta so o tempo dentro da janela de atendimento configurada em
                <em> /admin/atendimento</em>: se o cliente manda 17:58 e o atendente responde 07:00
                do dia seguinte com janela 07-18, o TTFR fica 2 min, nao 13h.
              </dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>TTR</dt>
              <dd style={{ margin: 0 }}>
                <strong>Time to Resolve</strong> — tempo <em>util</em> entre a criacao e o
                fechamento do chamado. Mesma regra do TTFR: conta apenas o tempo dentro da janela
                de atendimento.
              </dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>Mediana</dt>
              <dd style={{ margin: 0 }}>
                Valor que divide a amostra ao meio (50% acima, 50% abaixo). Menos sensivel a
                casos extremos do que a media.
              </dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>% SLA TTFR / TTR</dt>
              <dd style={{ margin: 0 }}>
                Dos chamados avaliados, quantos % cumpriram o limite cadastrado em
                <em> /admin/atendimento</em> para a prioridade do chamado. Verde &ge; 90%,
                laranja entre 70% e 89%, vermelho abaixo.
              </dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>Origem</dt>
              <dd style={{ margin: 0 }}>
                <em>WhatsApp</em> = chamado criado pelo bot a partir de uma conversa.
                <em> Manual</em> = aberto direto pela tela de chamados.
              </dd>

              <dt style={{ color: 'var(--portal-text)', fontWeight: 600 }}>Avaliados</dt>
              <dd style={{ margin: 0 }}>
                Quantos chamados tem dado suficiente para o calculo (ex: TTFR so conta
                chamados que ja receberam uma primeira resposta).
              </dd>
            </dl>
          </details>
        </>
      )}
    </div>
  );
}
